/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import { CloseModalProps } from '../modal-helpers';
import activateAutomationSrc from './images/activate-automation.png';
import connectEmailSrc from './images/connect-email.png';
import installXraySrc from './images/install-x-ray.png';
import sendEmailsSrc from './images/send-emails.png';
import welcomeAgencySrc from './images/welcome-agency.png';
import styles from './WelcomeSeriesModal.module.css';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import {
  activeChatbotIdSelector,
  activeCompanySelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { StoreState } from '@mm/state/store-interfaces';
import { selectRecordsByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import {
  selectCurrentUser,
  selectCurrentUserId,
} from '@mm/state/selectors/userSelector';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { useCreateGetemails } from '../../../dashboard/pages/XRayOnboarding/hooks/use-create-getemails';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { Spin } from 'antd';
import { useResolutionInfo } from '@mm/hooks';

type Steps =
  | 'getStarted'
  | 'installXray'
  | 'connectEmail'
  | 'activateAutomation'
  | 'sendEmails';

type StepObject = {
  id: number;
  header: string;
  subheader: string;
  image: string;
  imageStyle: { width: string };
  button?: string;
  onClick?: () => void;
  nextStep: Steps;
  prevStep: Steps;
  customButtonComponent?: any;
};

export const WelcomeSeriesModal = (
  props: { onModalClose: () => void } & CloseModalProps,
) => {
  const { closeModal, onModalClose } = props;
  const { campaigns: automations } =
    useSelector((state: StoreState) =>
      selectRecordsByUiFilterKind(state, 'automation'),
    ) || {};
  const user = useSelector(selectCurrentUser);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const companyId = useSelector(selectActiveCompanyId);
  const getEmailsPromoter = useSelector(
    (state: StoreState) => state.promoter.entities,
  )?.find(
    promoter => promoter.kind === 'getemails' && promoter.botId === chatbotId,
  );

  const [activeStep, setActiveStep] = useState<Steps>('getStarted');
  const [isImageLoading, setIsImageLoading] = useState(false);

  const history = useHistory();
  const { updateUserAttributes } = useUsersCRUDActions();
  const { retrieveAll } = useCampaignsOverviewCRUDActions();
  const { createGetemailsCampaign } = useCreateGetemails();
  const { openActivateEmailAccountModal } = useModalActions();
  const { showSlideoutMenu } = useViewActions();

  useEffect(() => {
    if (user?.id) {
      updateUserAttributes(companyId, user.id, {
        seenWelcomeSeriesModal: true,
      });
    }
  }, []);

  useEffect(() => {
    if (chatbotId > 0) {
      try {
        retrieveAll({
          botId: chatbotId,
          kind: 'automation',
          pagination: {
            page: 1,
            perPage: 8,
          },
        });
      } catch (e) {}
    }
  }, [chatbotId]);

  const redirectToAutomation = () => {
    if (automations?.length) {
      history.push(
        BotBuilderRoutes.automationEdit.getLink({
          chatbotId,
          id: automations[0].id,
        }),
      );
      if (getEmailsPromoter) {
        showSlideoutMenu('getemailsEditorNew', {
          promoterId: getEmailsPromoter.id,
        });
      }
    } else {
      createGetemailsCampaign(chatbotId, true);
    }
    closeModal();
  };

  const onClose = () => {
    closeModal();
    onModalClose?.();
  };

  const handleStepChange = (step: Steps) => {
    if (step) {
      setIsImageLoading(true);
      setActiveStep(step);
    }
  };

  const STEPS = {
    installXray: {
      id: 1,
      header: 'Install X-Ray Pixel to capture your 50 free leads',
      subheader:
        'Use our X-Ray Pixel to automatically capture leads from your website visitors and contact them with our automation tools.',
      image: installXraySrc,
      imageStyle: { width: '584px' },
      button: 'Install X-Ray Pixel',
      onClick: () => {
        history.push(BotBuilderRoutes.xRay.getLink({ chatbotId }));
        closeModal();
      },
      nextStep: 'connectEmail',
    } as StepObject,
    connectEmail: {
      id: 2,
      header: 'Connect an email account',
      subheader:
        'In order to contact your new warm, high intent leads you must connect your email.',
      image: connectEmailSrc,
      imageStyle: { width: '200px' },
      button: 'Connect Email',
      onClick: openActivateEmailAccountModal,
      prevStep: 'installXray',
      nextStep: 'activateAutomation',
    } as StepObject,
    activateAutomation: {
      id: 3,
      header: 'Activate outbound automations to land in the Inbox every time!',
      subheader:
        'Use our X-Ray Pixel to automatically capture leads from your website visitors and contact them with our automation tools.',
      image: activateAutomationSrc,
      imageStyle: { width: '480px' },
      button: 'Activate Automation',
      onClick: redirectToAutomation,
      prevStep: 'connectEmail',
      nextStep: 'sendEmails',
    } as StepObject,
    sendEmails: {
      id: 4,
      header: 'Start sending emails',
      subheader:
        'In order to contact your new warm, high intent leads you must connect your email',
      image: sendEmailsSrc,
      imageStyle: { width: '485px' },
      prevStep: 'activateAutomation',
      customButtonComponent: (
        <PrimaryButton
          className={classNames(styles.button, styles.customButton)}
          onClick={redirectToAutomation}
        >
          Get Started
        </PrimaryButton>
      ),
    } as StepObject,
  };
  return activeStep === 'getStarted' ? (
    <GetStartedModal
      closeModal={onClose}
      onClick={() => setActiveStep('installXray')}
    />
  ) : (
    <div className={styles.container}>
      <CloseOutlined className={styles.close} onClick={onClose} />
      <div className={styles.content}>
        <div className={styles.step}>{STEPS[activeStep].id} of 4</div>
        <div className={styles.header}>{STEPS[activeStep].header}</div>
        <div className={classNames(styles.header, styles.headerSmall)}>
          {STEPS[activeStep].subheader}
        </div>
        <Spin spinning={isImageLoading} size="small" className={styles.spin}>
          <img
            src={STEPS[activeStep].image}
            className={styles.image}
            style={
              isImageLoading
                ? { display: 'none' }
                : STEPS[activeStep].imageStyle
            }
            onLoad={() => setIsImageLoading(false)}
          />
        </Spin>
        {STEPS[activeStep].button && (
          <PrimaryButton
            className={styles.button}
            onClick={STEPS[activeStep].onClick}
          >
            {STEPS[activeStep].button}
          </PrimaryButton>
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.buttons}>
          {STEPS[activeStep]?.prevStep && (
            <PrimaryButton
              color="transparent-no-shadow"
              className={styles.buttonSecondary}
              onClick={() => handleStepChange(STEPS[activeStep].prevStep)}
            >
              <ArrowLeftOutlined className={styles.arrow} /> Back
            </PrimaryButton>
          )}
          {STEPS[activeStep]?.nextStep && (
            <PrimaryButton
              color="transparent-no-shadow"
              className={styles.buttonSecondary}
              onClick={() => handleStepChange(STEPS[activeStep].nextStep)}
            >
              Next step
              <ArrowRightOutlined className={styles.arrow} />
            </PrimaryButton>
          )}
          {STEPS[activeStep].customButtonComponent
            ? STEPS[activeStep].customButtonComponent
            : null}
        </div>
      </div>
    </div>
  );
};

const GetStartedModal = ({ closeModal, onClick }) => {
  const iframeMouseOverRef = useRef(false);
  const { screenWidth } = useResolutionInfo();

  const { updateUserAttributes } = useUsersCRUDActions();

  const activeCompany = useSelector(activeCompanySelector);
  const companyId = useSelector(selectActiveCompanyId);
  const currentUserId = useSelector(selectCurrentUserId);
  const isAgency = activeCompany?.businessType === 'agency';

  const [startedWatchingVideo, setStartedWatchingVideo] = useState(false);

  const onClose = () => {
    onClick();
    closeModal();
  };

  useEffect(() => {
    if (companyId && currentUserId && startedWatchingVideo) {
      // Note: considering seen if the user has not just opened the modal, but also watched the video
      const timeoutHandle = setTimeout(() => {
        updateUserAttributes(companyId, currentUserId, {
          stopShowingWalkthroughVideo: true,
        });
      }, 2.5 * 60 * 1000); // Note: video is ~5 minutes, but users can watch it a faster speed

      return () => {
        clearTimeout(timeoutHandle);
      };
    }
  }, [companyId, currentUserId, startedWatchingVideo]);

  const onWindowBlur = () => {
    // Note: this allows us to track if the user clicked the 'play' button in the iframe
    if (iframeMouseOverRef.current) {
      setStartedWatchingVideo(true);
    }
  };

  useEffect(() => {
    window.focus();
    window.addEventListener('blur', onWindowBlur);

    return () => {
      window.removeEventListener('blur', onWindowBlur);
    };
  }, []);

  if (screenWidth < 700) {
    return null;
  }

  if (isAgency && !activeCompany.consideredQualified) {
    return (
      <div className={styles.getStartedAgency}>
        <CloseOutlined className={styles.close} onClick={onClose} />
        <div className={styles.getStartedAgencyModalContent}>
          <div className={styles.header}>
            Welcome to Your Powerful Identity Resolution Platform
          </div>
          <div className={styles.headerSmall}>
            Your 7-day trial with 500 IDs starts now!
          </div>
          <div className={styles.text}>
            Install the X-Ray Website Visitor ID pixel on your site to start
            converting anonymous visitors into identified new and existing
            customer profiles.
          </div>
          <PrimaryButton className={styles.button} onClick={onClose}>
            Install the Pixel
          </PrimaryButton>
        </div>
        <img src={welcomeAgencySrc} alt="Welcome Agency" />
      </div>
    );
  }

  return (
    <div className={styles.getStartedModal}>
      <CloseOutlined className={styles.close} onClick={onClose} />
      <div className={styles.getStartedModalContent}>
        <div className={styles.header}>Welcome to Customers.ai</div>
        <div className={styles.headerSmall}>
          <span>Our goal is to get you </span>
          <span className={styles.textHighlighted}>
            more leads, more revenue,
          </span>
          <span> and better insight into your marketing.</span>
        </div>
        <div
          className={styles.videoIframeContainer}
          onMouseOver={() => {
            iframeMouseOverRef.current = true;
          }}
          onMouseOut={() => {
            window.focus();
            iframeMouseOverRef.current = false;
          }}
        >
          <iframe
            width="640"
            height="394"
            src="https://player.vimeo.com/video/1029670735?h=4265e990d9&color=3454EF&title=0&byline=0"
            allowFullScreen
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            title="X-Ray Pixel Email Capture System in 90 Seconds"
          />
        </div>
      </div>
    </div>
  );
};
